import {updateBid} from "./bidding_api";
import {UserContext} from "../../contexts/UserContext";
import {BidBaseModel, BidStatus} from "./bidding_model";
import {AutosizeTextarea} from "../ui/autoresizeTextArea";
import {Button} from "../ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,} from "../ui/form";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "../ui/select";
import {Input} from "../ui/input";
import {SubmitBidDialog} from "./SubmitBid";
import {WithLoggedInAuthInfoProps, withRequiredAuthInfo,} from "@propelauth/react";
import React, {useContext, useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useToast} from "../ui/use-toast";
import {Cell, cellsEncoder,} from "../project_conf/bidConfig_model";
import {CompanyType} from "../../models/company";
import {postWithToken} from "../../api/apiBase";
import {USDonChange, USDtoValue} from "../fields/USDField";

type BidFormProps = WithLoggedInAuthInfoProps & {
  editable?: boolean;
  create?: boolean;
  bid: BidBaseModel;
  isEditing: boolean;
  onSave: (data: BidBaseModel) => void;
  setIsEditing: (v: boolean) => void;
  onSubmit: (data: BidBaseModel) => void;
};

function CreateBidForm({
  accessToken,
  create,
  editable,
    bid,
    onSave,
    isEditing,
    setIsEditing,
    onSubmit,
  ...props
}: BidFormProps) {

  const { project_id, bid_id } = useParams<string>();
  const userContext = useContext(UserContext);
  const { toast } = useToast();
  const [responseStatus, setResponseStatus] = useState(bid.response_status===null ? undefined : bid.response_status)


  const canEdit =
    userContext?.company_type === CompanyType.builder &&
    bid.status !== BidStatus.Submitted;

  const form = useForm<BidBaseModel>({
    defaultValues: bid
  });

  const onSubmitBid = () => {
      let values = form.getValues();
      onSubmit(values);
  }


  const onSetResponseStatus = (resp_status: any) => {
    setResponseStatus(resp_status)
    postWithToken(
      `/projects/${project_id}/bidding/${bid_id}/developer_update`,
      JSON.stringify({ response_status: resp_status }),
      accessToken as string
    )
      .then((data) => {
        toast({
          title: "Success!",
          description: "Status updated successfully.",
          variant: "default",
        });
        // setIsEditing(false);
        // navigate(`/project/${project_id}/bidders`);
      })
      .catch((error) => {
        console.error("Error submitting bid:", error);
        toast({
          title: "Error",
          description: JSON.stringify(error) || "Failed to udpate status.",
          variant: "destructive",
        });
      });
  };
  

  return (
      <Card>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit(onSave)();
            }}
          >
            <CardHeader>
              <CardTitle className="columns-2 flex justify-between">
                <div>Bid</div>
                {form.getValues().status === BidStatus.Submitted &&
                userContext?.company_type === CompanyType.developer ? (
                  <Select
                      onValueChange={onSetResponseStatus}
                      value={responseStatus }>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Set Response" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="in_review">In Review</SelectItem>
                        {/*<SelectItem value="revise_resubmit">Revise and Resubmit</SelectItem>*/}
                      <SelectItem value="validated">Validated</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <></>
                )}
                {form.getValues().status === BidStatus.Submitted &&
                userContext?.company_type === CompanyType.builder ? (
                  <div
                    className={`p-2 rounded-lg my-2 ${
                      form.getValues().response_status === "rejected"
                        ? "bg-red-100 text-red-800 border border-red-300"
                        : form.getValues().response_status === "in_review"
                        ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                        : (form.getValues().response_status as string) ===
                          "validated"
                        ? "bg-green-100 text-green-800 border border-green-300"
                        : ""
                    }`}
                  >
                    <p>{form.getValues().response_status}</p>
                  </div>
                ) : (
                  <></>
                )}
              </CardTitle>
              {canEdit ? (
                <div className="columns-2 flex justify-between">
                  {isEditing ? (
                    <div className="items-center flex justify-between">
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button variant="outline" type="submit">
                        Save Bid
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Button type="button" onClick={() => setIsEditing(true)}>
                        Edit
                      </Button>
                    </div>
                  )}
                  <SubmitBidDialog onSubmit={onSubmitBid} />
                </div>
              ) : (
                <></>
              )}
            </CardHeader>
            <CardContent>
              <div className="columns-2">
                <div className="">
                  <FormField
                    control={form.control}
                    name="bid_amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Amount</FormLabel>
                        <FormControl>
                          <Input
                            placeholder=""
                            {...field}
                            value={USDtoValue(field.value)}
                            className="text-lg disabled:cursor-allowed disabled:opacity-100 disabled:border-none"
                            disabled={!isEditing}
                            onChange={(e) => {
                              field.onChange(USDonChange(e.target.value));
                            }}
                          />
                        </FormControl>
                        <FormDescription></FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder=""
                            {...field}
                            className="disabled:cursor-allowed disabled:opacity-100 disabled:border-none"
                            disabled={!isEditing}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormDescription></FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Narrative</FormLabel>
                    <FormControl>
                      <AutosizeTextarea
                        placeholder=""
                        {...field}
                        className="disabled:cursor-allowed disabled:opacity-100 disabled:border-none"
                        disabled={!isEditing}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormDescription></FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="exclusions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Exclusions</FormLabel>
                    <FormControl>
                      <AutosizeTextarea
                        placeholder=""
                        className="disabled:cursor-allowed disabled:opacity-100 disabled:border-none"
                        {...field}
                        disabled={!isEditing}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormDescription></FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </form>
        </Form>
      </Card>


  );
}

export default withRequiredAuthInfo(CreateBidForm);
